import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './AdminKpiReport.css';

const RANGE_OPTIONS = [14, 30, 90];
const MOUNTAIN_TYPE_NAMES = {
  PSGL: '完全攻略ストラテジスト',
  PSGC: '安全登頂リーダー',
  PSEL: '静寂の体験求道者',
  PSEC: '自然愛でる安全隊',
  PAGL: '孤独な山頂ハンター',
  PAGC: 'チーム突破型',
  PAEL: '自由な体験ソロイスト',
  PAEC: '山の楽しさ全力グループ',
  ISGL: '直感慎重な登頂者',
  ISGC: 'のんびり安全グループ',
  ISEL: '孤独な山の詩人',
  ISEC: '自然派お散歩グループ',
  IAGL: '野生の登頂マシン',
  IAGC: '突撃チームリーダー',
  IAEL: '風まかせ一人旅',
  IAEC: '山の無法者グループ',
};
const KPI_TARGETS = [
  { key: 'visitToSignupRate', label: '訪問→登録 CVR', targetLabel: '15%以上', threshold: 15, unit: 'percent', higherIsBetter: true },
  { key: 'signupToProfileCompleteRate', label: '登録→プロフィール入力率', targetLabel: '60%以上', threshold: 60, unit: 'percent', higherIsBetter: true },
  { key: 'signupToRecruitmentRate', label: '登録→募集投稿率', targetLabel: '20%以上', threshold: 20, unit: 'percent', higherIsBetter: true },
  { key: 'signupToApplyRate', label: '登録→初応募率', targetLabel: '35%以上', threshold: 35, unit: 'percent', higherIsBetter: true },
  { key: 'recruitmentToApplicationRate', label: '募集→応募率', targetLabel: '50%以上', threshold: 50, unit: 'percent', higherIsBetter: true },
  { key: 'applicationToMatchRate', label: '応募→マッチ率', targetLabel: '30%以上', threshold: 30, unit: 'percent', higherIsBetter: true },
  { key: 'secondMatchRate', label: '2回目マッチ率', targetLabel: '40%以上', threshold: 40, unit: 'percent', higherIsBetter: true },
  { key: 'applicationToChatRate', label: '応募→チャット開始率', targetLabel: '70%以上', threshold: 70, unit: 'percent', higherIsBetter: true },
  { key: 'chatToMatchRate', label: 'チャット→マッチ率', targetLabel: '25%以上', threshold: 25, unit: 'percent', higherIsBetter: true },
];

const formatInteger = (value) => new Intl.NumberFormat('ja-JP').format(value || 0);
const formatPercent = (value) => (value == null ? '-' : `${value.toFixed(1)}%`);
const formatNumber = (value, suffix = '') => (value == null ? '-' : `${new Intl.NumberFormat('ja-JP').format(value)}${suffix}`);
const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function getStatusClassName(value, threshold, higherIsBetter) {
  if (value == null) return 'is-muted';
  const hit = higherIsBetter ? value >= threshold : value <= threshold;
  if (hit) return 'is-good';
  const warning = higherIsBetter ? value >= threshold * 0.7 : value <= threshold * 1.5;
  return warning ? 'is-warn' : 'is-bad';
}

const labelMaps = {
  tripTypeCounts: {
    day_hike: '日帰り',
    hut_stay: '小屋泊',
    tent_stay: 'テント泊',
    multi_day: '縦走・複数日',
  },
  transportModeCounts: {
    public_transport: '公共交通',
    car: '車（レンタカー）',
    carpool: '車（相乗り）',
    mixed: '公共交通・車どちらも可',
  },
  meetingAreaTypeCounts: {
    station: '駅集合',
    trailhead: '登山口集合',
    local: '現地集合',
    other: 'その他',
  },
  experienceLevelTargetCounts: {
    beginner_welcome: '初心者歓迎',
    beginner: '初級者向け',
    intermediate: '中級者向け',
    advanced: '上級者向け',
  },
  carShareAvailableCounts: {
    available: '相乗り可',
    not_available: '相乗り不可',
    consult: '相談可',
  },
  weatherThresholdCounts: {
    light_rain_ok: '小雨決行',
    rain_cancel: '雨天中止',
    storm_cancel: '荒天中止',
    decide_previous_day: '前日判断',
  },
  estimatedBudgetBandCounts: {
    under_3000: '3,000円未満',
    '3000_5000': '3,000〜5,000円',
    '5000_10000': '5,000〜10,000円',
    '10000_20000': '10,000〜20,000円',
    over_20000: '20,000円以上',
  },
  dangerCounts: {
    chain: '鎖場',
    rock: '岩場',
    snow: '雪渓・残雪',
    river_crossing: '渡渉',
    steep_descent: '急登・急下降',
  },
  equipmentRequirementCounts: {
    helmet: 'ヘルメット',
    chain_spike: 'チェーンスパイク',
    trekking_pole: 'トレッキングポール',
    crampon: 'アイゼン',
    first_aid: 'ファーストエイドキット',
    hardshell: 'ハードシェル',
    avalanche_gear: '雪崩装備',
    rainwear: 'レインウェア',
    headlamp: 'ヘッドライト',
  },
  preHikeConcernCounts: {
    fitness: '体力不安',
    equipment: '装備不足不安',
    navigation: '道迷い不安',
    weather: '天候不安',
    first_meeting: '初対面同行への不安',
    meeting_place: '集合場所への不安',
    transport: '交通手段への不安',
  },
  equipmentShortageCategoryCounts: {
    clothing: 'ウェア・防寒',
    rain_gear: '雨具',
    footwear: '靴・足まわり',
    food_water: '水・行動食',
    navigation: '地図・GPS',
    safety: 'ヘルメット・救急用品など安全装備',
    snow_gear: '雪山・凍結対策装備',
    other: 'その他',
  },
  stopoverCategoryCounts: {
    hot_spring: '温泉',
    meal: '食事',
    shop: '買い物',
    station: '道の駅・駅',
    sightseeing: '観光',
    none: '立ち寄りなし',
    other: 'その他',
  },
  nextMountainAreaCounts: {
    north_alps: '北アルプス',
    central_alps: '中央アルプス',
    south_alps: '南アルプス',
    yatsugatake: '八ヶ岳',
    tanigawa: '谷川・上信越',
    okuchichibu: '奥秩父',
    kanto: '関東近郊',
    kansai: '関西近郊',
    kyushu: '九州',
    other: 'その他',
  },
};

function countMapToRows(map, labelMap = {}) {
  return Object.entries(map || {})
    .map(([key, value]) => ({ label: labelMap[key] || key, value }))
    .sort((a, b) => b.value - a.value);
}

function AdminKpiReport() {
  const { currentUser } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [moderationUpdatingId, setModerationUpdatingId] = useState('');
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotMessage, setSnapshotMessage] = useState('');
  const [typeStats, setTypeStats] = useState([]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tokenResult = await currentUser.getIdTokenResult();
      if (tokenResult.claims?.admin !== true) {
        setReport(null);
        setError('管理者権限がありません。');
        return;
      }
      const callable = httpsCallable(functions, 'getAdminKpiReport');
      const response = await callable({ days });
      setReport(response.data);
    } catch (fetchError) {
      console.error('Failed to load admin KPI report:', fetchError);
      setReport(null);
      setError('管理レポートを表示できません。');
    } finally {
      setLoading(false);
    }
  }, [currentUser, days]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'mountainTypeStats'), orderBy('count', 'desc')));
        setTypeStats(snap.docs.map((d) => ({ typeCode: d.id, count: d.data().count || 0 })));
      } catch (_) { /* 権限なし等は無視 */ }
    })();
  }, []);

  const handleModerateReport = async (reportId, decisionStatus, actionTaken) => {
    setModerationUpdatingId(reportId);
    setError('');
    try {
      const callable = httpsCallable(functions, 'moderateReport');
      await callable({ reportId, decisionStatus, actionTaken });
      await fetchReport();
    } catch (moderationError) {
      console.error('Failed to update moderation decision:', moderationError);
      setError('通報の判定更新に失敗しました。');
    } finally {
      setModerationUpdatingId('');
    }
  };

  const handleGenerateSnapshot = async () => {
    setSnapshotSaving(true);
    setSnapshotMessage('');
    setError('');
    try {
      const callable = httpsCallable(functions, 'generateKpiSnapshot');
      await callable({ days });
      setSnapshotMessage('集計スナップショットを保存しました。');
    } catch (snapshotError) {
      console.error('Failed to generate KPI snapshot:', snapshotError);
      setError('集計スナップショットの保存に失敗しました。');
    } finally {
      setSnapshotSaving(false);
    }
  };

  const summaryCards = useMemo(() => {
    if (!report?.summary) return [];
    return [
      { label: '新規登録数', value: formatInteger(report.summary.newUsers), tone: 'neutral' },
      { label: 'プロフィール入力数', value: formatInteger(report.summary.profileCompletes), tone: 'neutral' },
      { label: '募集閲覧数', value: formatInteger(report.summary.recruitmentViews), tone: 'neutral' },
      { label: '募集投稿数', value: formatInteger(report.summary.recruitmentPosts), tone: 'neutral' },
      { label: '応募数', value: formatInteger(report.summary.applications), tone: 'neutral' },
      { label: 'チャット開始数', value: formatInteger(report.summary.chatStarts), tone: 'neutral' },
      { label: 'マッチ件数', value: formatInteger(report.summary.hikeCompletions || report.summary.matches), tone: 'neutral' },
      { label: '体験談投稿数', value: formatInteger(report.summary.storyPosts), tone: 'accent' },
    ];
  }, [report]);

  const targetCards = useMemo(() => {
    if (!report?.kpis) return [];
    return KPI_TARGETS.map((target) => ({
      ...target,
      value: report.kpis[target.key],
      statusClassName: getStatusClassName(report.kpis[target.key], target.threshold, target.higherIsBetter),
    }));
  }, [report]);

  const funnelRows = useMemo(() => {
    if (!report?.funnel) return [];
    return [
      { label: '訪問数', value: report.funnel.visits, cvr: null, target: null },
      { label: '新規登録数', value: report.funnel.newUsers, cvr: report.funnel.visitToSignupRate, target: 15 },
      { label: 'プロフィール入力数', value: report.funnel.profileCompletes, cvr: report.funnel.signupToProfileCompleteRate, target: 60 },
      { label: '募集投稿数', value: report.funnel.signupPosters, cvr: report.funnel.signupToRecruitmentRate, target: 20 },
      { label: '募集閲覧数', value: report.funnel.recruitmentViews, cvr: null, target: null },
      { label: '応募数', value: report.funnel.applications, cvr: report.funnel.recruitmentViewToApplicationRate, target: 50 },
      { label: 'マッチ件数', value: report.funnel.matches, cvr: report.funnel.applicationToMatchRate, target: 30 },
    ];
  }, [report]);

  return (
    <div className="admin-kpi-page">
      <div className="admin-kpi-shell">
        <header className="admin-kpi-hero">
          <p className="admin-kpi-hero__eyebrow">Private Report</p>
          <h1>管理用 KPI ダッシュボード</h1>
          <p>毎日見る主要 KPI と、下部の通報・NGワード検知ログをまとめて確認できます。</p>
        </header>

        <section className="admin-kpi-toolbar">
          <div className="admin-kpi-toolbar__group" role="tablist" aria-label="表示期間">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`admin-kpi-chip ${days === option ? 'is-active' : ''}`}
                onClick={() => setDays(option)}
              >
                直近 {option} 日
              </button>
            ))}
            <button
              type="button"
              className="admin-kpi-chip"
              onClick={handleGenerateSnapshot}
              disabled={snapshotSaving || loading || !report}
            >
              {snapshotSaving ? '保存中...' : 'スナップショット保存'}
            </button>
          </div>
          <div className="admin-kpi-toolbar__meta">
            <p>最終更新: {formatDateTime(report?.generatedAt)}</p>
            <p>通報 3 件で非表示、7 日で 3 件通報で投稿停止です。</p>
            {snapshotMessage && <p>{snapshotMessage}</p>}
          </div>
        </section>

        {loading && <div className="admin-kpi-state">読み込み中です...</div>}
        {!loading && error && <div className="admin-kpi-state is-error">{error}</div>}

        {!loading && !error && report && (
          <>
            <section className="admin-kpi-summary">
              {summaryCards.map((card) => (
                <article key={card.label} className={`admin-kpi-card ${card.tone === 'accent' ? 'is-accent' : ''}`}>
                  <p className="admin-kpi-card__label">{card.label}</p>
                  <p className="admin-kpi-card__value">{card.value}</p>
                </article>
              ))}
            </section>

            <section className="admin-kpi-targets">
              {targetCards.map((card) => (
                <article key={card.key} className={`admin-kpi-target-card ${card.statusClassName}`}>
                  <div className="admin-kpi-target-card__header">
                    <p>{card.label}</p>
                    <span>{card.targetLabel}</span>
                  </div>
                  <p className="admin-kpi-target-card__value">{formatPercent(card.value)}</p>
                </article>
              ))}
            </section>

            <section className="admin-kpi-funnel-card">
              <div className="admin-kpi-section-header">
                <div>
                  <p className="admin-kpi-section-header__eyebrow">Most Important</p>
                  <h2>ファネル</h2>
                  <p>どこで詰まっているかを一目で確認するための主要導線です。</p>
                </div>
              </div>

              <div className="admin-kpi-funnel">
                {funnelRows.map((row, index) => (
                  <React.Fragment key={row.label}>
                    <article className="admin-kpi-funnel-step">
                      <p className="admin-kpi-funnel-step__label">{row.label}</p>
                      <p className="admin-kpi-funnel-step__value">{formatInteger(row.value)}</p>
                    </article>
                    {index < funnelRows.length - 1 && (
                      <div
                        className={`admin-kpi-funnel-cvr ${
                          row.cvr == null
                            ? 'is-muted'
                            : row.cvr >= (funnelRows[index + 1].target || 0)
                              ? 'is-good'
                              : row.cvr >= ((funnelRows[index + 1].target || 0) * 0.7)
                                ? 'is-warn'
                                : 'is-bad'
                        }`}
                      >
                        <span>CVR {funnelRows[index + 1].target ? `(目標 ${funnelRows[index + 1].target}%)` : ''}</span>
                        <strong>{formatPercent(funnelRows[index + 1].cvr)}</strong>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>

            <section className="admin-kpi-targets">
              <article className="admin-kpi-target-card">
                <div className="admin-kpi-target-card__header">
                  <p>初回マッチまで</p>
                  <span>24時間以内</span>
                </div>
                <p className="admin-kpi-target-card__value">{formatNumber(report.kpis.averageFirstMatchHours, '時間')}</p>
              </article>
              <article className="admin-kpi-target-card">
                <div className="admin-kpi-target-card__header">
                  <p>平均歩行時間</p>
                  <span>募集入力ベース</span>
                </div>
                <p className="admin-kpi-target-card__value">{formatNumber(report.dataCollection?.averageWalkingHours, '時間')}</p>
              </article>
              <article className="admin-kpi-target-card">
                <div className="admin-kpi-target-card__header">
                  <p>平均標高差</p>
                  <span>募集入力ベース</span>
                </div>
                <p className="admin-kpi-target-card__value">{formatNumber(report.dataCollection?.averageElevationGain, 'm')}</p>
              </article>
              <article className="admin-kpi-target-card">
                <div className="admin-kpi-target-card__header">
                  <p>山行後満足度</p>
                  <span>任意回答</span>
                </div>
                <p className="admin-kpi-target-card__value">{formatNumber(report.dataCollection?.averageSatisfaction, '/5')}</p>
              </article>
            </section>

            <section className="admin-kpi-table-card">
              <div className="admin-kpi-section-header">
                <div>
                  <p className="admin-kpi-section-header__eyebrow">Daily Trend</p>
                  <h2>日別推移</h2>
                  <p>訪問、募集、応募、マッチ、体験談投稿の推移です。</p>
                </div>
              </div>

              <div className="admin-kpi-table-wrap">
                <table className="admin-kpi-table">
                  <thead>
                    <tr>
                      <th>日付</th>
                      <th>訪問数</th>
                      <th>募集閲覧</th>
                      <th>新規登録</th>
                      <th>募集投稿</th>
                      <th>応募数</th>
                      <th>チャット開始</th>
                      <th>マッチ</th>
                      <th>体験談</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.daily.map((row) => (
                      <tr key={row.date}>
                        <td>{row.date}</td>
                        <td>{formatInteger(row.visits)}</td>
                        <td>{formatInteger(row.recruitmentViews)}</td>
                        <td>{formatInteger(row.newUsers)}</td>
                        <td>{formatInteger(row.recruitmentPosts)}</td>
                        <td>{formatInteger(row.applications)}</td>
                        <td>{formatInteger(row.chatStarts)}</td>
                        <td>{formatInteger(row.matches)}</td>
                        <td>{formatInteger(row.storyPosts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="admin-kpi-log-grid">
              {[
                ['山行タイプ', 'tripTypeCounts'],
                ['移動手段', 'transportModeCounts'],
                ['集合場所タイプ', 'meetingAreaTypeCounts'],
                ['対象経験レベル', 'experienceLevelTargetCounts'],
                ['相乗り可否', 'carShareAvailableCounts'],
                ['天候判断', 'weatherThresholdCounts'],
                ['想定予算', 'estimatedBudgetBandCounts'],
                ['危険箇所', 'dangerCounts'],
                ['必要装備', 'equipmentRequirementCounts'],
                ['応募前の不安', 'preHikeConcernCounts'],
                ['装備不足カテゴリ', 'equipmentShortageCategoryCounts'],
                ['下山後立ち寄りカテゴリ', 'stopoverCategoryCounts'],
                ['次に行きたい山域', 'nextMountainAreaCounts'],
              ].map(([title, key]) => (
                <section className="admin-kpi-table-card" key={key}>
                  <div className="admin-kpi-section-header">
                    <div>
                      <p className="admin-kpi-section-header__eyebrow">Collected Data</p>
                      <h2>{title}</h2>
                    </div>
                  </div>
                  <div className="admin-kpi-table-wrap">
                    <table className="admin-kpi-table">
                      <thead>
                        <tr>
                          <th>項目</th>
                          <th>件数</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countMapToRows(report.dataCollection?.[key], labelMaps[key]).map((row) => (
                          <tr key={row.label}>
                            <td>{row.label}</td>
                            <td>{formatInteger(row.value)}</td>
                          </tr>
                        ))}
                        {countMapToRows(report.dataCollection?.[key], labelMaps[key]).length === 0 && (
                          <tr>
                            <td colSpan="2">データなし</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
              <section className="admin-kpi-table-card">
                <div className="admin-kpi-section-header">
                  <div>
                    <p className="admin-kpi-section-header__eyebrow">Optional Survey</p>
                    <h2>任意アンケート</h2>
                  </div>
                </div>
                <div className="admin-kpi-table-wrap">
                  <table className="admin-kpi-table">
                    <tbody>
                      <tr><td>応募前アンケート回答</td><td>{formatInteger(report.dataCollection?.preHikeSurveyResponses)}</td></tr>
                      <tr><td>山行後アンケート回答</td><td>{formatInteger(report.dataCollection?.postHikeSurveyResponses)}</td></tr>
                      <tr><td>実施できた回答</td><td>{formatInteger(report.dataCollection?.completedHikeResponses)}</td></tr>
                      <tr><td>装備不足あり率</td><td>{formatPercent(report.dataCollection?.equipmentShortageRate)}</td></tr>
                      <tr><td>下山後立ち寄り回答</td><td>{formatInteger(report.dataCollection?.stopoverResponses)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </section>

            <section className="admin-kpi-log-grid">
              <section className="admin-kpi-table-card">
                <div className="admin-kpi-section-header">
                  <div>
                    <p className="admin-kpi-section-header__eyebrow">Moderation</p>
                    <h2>最新通報</h2>
                    <p>同一対象への通報が {report.moderation?.reportHideThreshold || 3} 件で自動非表示です。</p>
                  </div>
                </div>
                <div className="admin-kpi-table-wrap">
                  <table className="admin-kpi-table">
                    <thead>
                      <tr>
                        <th>日時</th>
                        <th>種別</th>
                        <th>対象</th>
                        <th>理由</th>
                        <th>判定</th>
                        <th>対応</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.moderation?.recentReports || []).map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTime(row.createdAt)}</td>
                          <td>{row.type || '-'}</td>
                          <td>{row.targetId || '-'}</td>
                          <td>{row.reason || '-'}</td>
                          <td>{row.decisionStatus || '-'}</td>
                          <td>{row.actionTaken || '-'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                disabled={moderationUpdatingId === row.id}
                                onClick={() => handleModerateReport(row.id, 'under_review', 'none')}
                              >
                                確認中
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline"
                                disabled={moderationUpdatingId === row.id}
                                onClick={() => handleModerateReport(row.id, 'no_action', 'none')}
                              >
                                対応不要
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={moderationUpdatingId === row.id}
                                onClick={() => handleModerateReport(row.id, 'action_completed', 'warning')}
                              >
                                対応済み
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(report.moderation?.recentReports || []).length === 0 && (
                        <tr>
                          <td colSpan="7">通報データなし</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="admin-kpi-table-card">
                <div className="admin-kpi-section-header">
                  <div>
                    <p className="admin-kpi-section-header__eyebrow">Moderation</p>
                    <h2>NGワード検知ログ</h2>
                    <p>保存は許可しつつ、警告表示と検知ログのみを残します。</p>
                  </div>
                </div>
                <div className="admin-kpi-table-wrap">
                  <table className="admin-kpi-table">
                    <thead>
                      <tr>
                        <th>日時</th>
                        <th>操作</th>
                        <th>対象</th>
                        <th>検知語</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.moderation?.moderationEvents || []).map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTime(row.createdAt)}</td>
                          <td>{row.action || '-'}</td>
                          <td>{row.contentType || '-'}</td>
                          <td>{(row.matchedTerms || []).join(', ') || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </section>
          </>
        )}

        {typeStats.length > 0 && (
          <section className="admin-kpi-table-card" style={{ marginTop: '32px' }}>
            <div className="admin-kpi-section-header">
              <div>
                <p className="admin-kpi-section-header__eyebrow">Tools</p>
                <h2>登山タイプ診断 結果ランキング</h2>
                <p>診断完了ごとに1カウント（累計）</p>
              </div>
            </div>
            <div className="admin-kpi-table-wrap">
              <table className="admin-kpi-table">
                <thead>
                  <tr>
                    <th>順位</th>
                    <th>タイプコード</th>
                    <th>タイプ名</th>
                    <th>回数</th>
                  </tr>
                </thead>
                <tbody>
                  {typeStats.map((row, i) => (
                    <tr key={row.typeCode}>
                      <td>{i + 1}</td>
                      <td>{row.typeCode}</td>
                      <td>{MOUNTAIN_TYPE_NAMES[row.typeCode] || '-'}</td>
                      <td>{formatInteger(row.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default AdminKpiReport;
